import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import toast from 'react-hot-toast';
import { getApiErrorMessage, toUserErrorMessage } from '../../utils/apiError';
import { useAuth } from '../../contexts/AuthContext';
import DocumentPreviewDialog from '../../components/Documents/DocumentPreviewDialog';
import ListLoadErrorAlert from '../../components/Common/ListLoadErrorAlert';
import { getAuthToken } from '../../lib/authToken';

type QueueTab = 'pending' | 'returned' | 'rejected';

const ExpenseApprovalsPage: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canApprove = Boolean(
    user?.role === 'admin' || user?.permissions?.['billing:write']
  );

  const [tab, setTab] = useState<QueueTab>('pending');
  const [reasonDialog, setReasonDialog] = useState<{
    id: string;
    action: 'rejected' | 'returned';
    description: string;
  } | null>(null);
  const [reason, setReason] = useState('');
  const [previewDoc, setPreviewDoc] = useState<{ id: string; title: string } | null>(null);

  const { data: items = [], isLoading, error, refetch } = useQuery({
    queryKey: ['expense-approvals', user?.tenantId, tab],
    queryFn: async () => {
      const res = await fetch(`/api/case-billing-items?type=expense&status=${tab}&limit=100`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Unable to load expenses'));
      const json = await res.json();
      return json.data?.billingItems || [];
    },
  });

  const statusMutation = useMutation({
    mutationFn: async ({
      id,
      status,
      statusReason,
    }: {
      id: string;
      status: string;
      statusReason?: string;
    }) => {
      const res = await fetch(`/api/case-billing-items/${id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status, statusReason }),
      });
      if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Update failed'));
      return res.json();
    },
    onSuccess: (_data, vars) => {
      const label =
        vars.status === 'approved'
          ? 'Expense approved'
          : vars.status === 'rejected'
            ? 'Expense rejected'
            : vars.status === 'returned'
              ? 'Expense returned'
              : 'Expense updated';
      toast.success(label);
      queryClient.invalidateQueries({ queryKey: ['expense-approvals'] });
      setReasonDialog(null);
      setReason('');
    },
    onError: (err: Error) => toast.error(toUserErrorMessage(err, 'Update failed')),
  });

  const totals = useMemo(() => {
    const sum = items.reduce((acc: number, item: any) => acc + Number(item.amount || 0), 0);
    return { count: items.length, sum };
  }, [items]);

  const openReason = (item: any, action: 'rejected' | 'returned') => {
    setReasonDialog({
      id: item.id,
      action,
      description: item.description || 'Expense',
    });
    setReason('');
  };

  const submitReason = () => {
    if (!reasonDialog) return;
    if (reason.trim().length < 3) {
      toast.error('Enter a reason (at least 3 characters)');
      return;
    }
    statusMutation.mutate({
      id: reasonDialog.id,
      status: reasonDialog.action,
      statusReason: reason.trim(),
    });
  };

  const statusChipColor = (status: string) => {
    if (status === 'pending') return 'warning';
    if (status === 'returned') return 'info';
    if (status === 'rejected') return 'error';
    return 'default';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Expense approvals
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Review pending expenses, view receipts, and approve / return / reject before invoicing.
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="pending" label="Pending" />
        <Tab value="returned" label="Returned" />
        <Tab value="rejected" label="Rejected" />
      </Tabs>

      {!canApprove && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You can view the queue; approving or rejecting requires billing write permission.
        </Alert>
      )}

      {error && (
        <ListLoadErrorAlert
          resourceLabel="expenses"
          message="The expense queue couldn’t be loaded right now. Try again shortly."
          onRetry={() => refetch()}
        />
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Alert severity="success">
          {tab === 'pending'
            ? 'No pending expenses to approve.'
            : `No ${tab} expenses.`}
        </Alert>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {totals.count} item(s) · ${totals.sum.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Case</TableCell>
                  <TableCell>Submitted by</TableCell>
                  <TableCell>Receipt</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item: any) => {
                  const receiptId = item.metadata?.receiptDocumentId;
                  return (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        {item.date
                          ? format(parseISO(String(item.date).slice(0, 10)), 'MMM d, yyyy')
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{item.description || '—'}</Typography>
                        {item.statusReason && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Reason: {item.statusReason}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{item.case?.caseNumber || '—'}</TableCell>
                      <TableCell>
                        {item.creator
                          ? `${item.creator.firstName} ${item.creator.lastName}`
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {receiptId ? (
                          <IconButton
                            size="small"
                            title="View receipt"
                            onClick={() =>
                              setPreviewDoc({
                                id: receiptId,
                                title: item.metadata?.receiptFileName || 'Receipt',
                              })
                            }
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell align="right">
                        $
                        {Number(item.amount || 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={item.status}
                          color={statusChipColor(item.status) as any}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {canApprove && (
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            {(tab === 'pending' || tab === 'returned') && (
                              <Button
                                size="small"
                                variant="contained"
                                disabled={statusMutation.isPending}
                                onClick={() =>
                                  statusMutation.mutate({ id: item.id, status: 'approved' })
                                }
                              >
                                Approve
                              </Button>
                            )}
                            {tab === 'pending' && (
                              <>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled={statusMutation.isPending}
                                  onClick={() => openReason(item, 'returned')}
                                >
                                  Return
                                </Button>
                                <Button
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  disabled={statusMutation.isPending}
                                  onClick={() => openReason(item, 'rejected')}
                                >
                                  Reject
                                </Button>
                              </>
                            )}
                            {tab === 'rejected' && (
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={statusMutation.isPending}
                                onClick={() =>
                                  statusMutation.mutate({ id: item.id, status: 'pending' })
                                }
                              >
                                Re-queue
                              </Button>
                            )}
                          </Stack>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Dialog
        open={Boolean(reasonDialog)}
        onClose={() => setReasonDialog(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {reasonDialog?.action === 'rejected' ? 'Reject expense' : 'Return expense'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {reasonDialog?.description}
          </Typography>
          <TextField
            label={reasonDialog?.action === 'rejected' ? 'Rejection reason' : 'Return reason'}
            fullWidth
            multiline
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Explain what needs to change or why this expense is denied"
            helperText="Required — shown to staff in the queue"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReasonDialog(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={reasonDialog?.action === 'rejected' ? 'error' : 'primary'}
            onClick={submitReason}
            disabled={statusMutation.isPending}
          >
            {reasonDialog?.action === 'rejected' ? 'Reject' : 'Return'}
          </Button>
        </DialogActions>
      </Dialog>

      {previewDoc && (
        <DocumentPreviewDialog
          open={Boolean(previewDoc)}
          onClose={() => setPreviewDoc(null)}
          document={previewDoc}
        />
      )}
    </Box>
  );
};

export default ExpenseApprovalsPage;
